import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Papa from 'papaparse';
import { getCurrentUser, fetchAuthSession } from 'aws-amplify/auth';
import { uploadFile, generateTestCases } from '../services/api';
import GenerateTestCasesDialog from './GenerateTestCasesDialog';
import TestCaseTable from './TestCaseTable';
import RefineChatPanel from './RefineChatPanel';

const MAX_BRD_SIZE = 20 * 1024 * 1024; // 20 MB
const VALID_BRD_EXTENSIONS = ['.pdf', '.docx'];

function FileUpload() {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [approvers, setApprovers] = useState(['']);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState('');
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [testCases, setTestCases] = useState([]);
  const [useGenerated, setUseGenerated] = useState(false);
  const [currentUserAlias, setCurrentUserAlias] = useState('');
  const [refinePanelOpen, setRefinePanelOpen] = useState(false);
  const [customFileName, setCustomFileName] = useState('');
  const [showFileNameDialog, setShowFileNameDialog] = useState(false);
  const [showReviewerPanel, setShowReviewerPanel] = useState(false);
  const [userStory, setUserStory] = useState('');

  // BRD Upload tile state
  const [selectedTile, setSelectedTile] = useState(null);
  const [brdFile, setBrdFile] = useState(null);
  const [brdError, setBrdError] = useState('');
  const [brdGenerating, setBrdGenerating] = useState(false);
  const [brdElapsed, setBrdElapsed] = useState(0);
  const brdTimerRef = useRef(null);
  const brdAbortRef = useRef(null);
  const brdDragActiveRef = useRef(false);
  const [brdDragActive, setBrdDragActive] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const session = await fetchAuthSession();
        const email = session.tokens?.idToken?.payload?.email || '';
        const user = await getCurrentUser();
        const raw = email || user.username || '';
        setCurrentUserAlias(raw.includes('@') ? raw.split('@')[0] : raw);
      } catch { /* not signed in */ }
    })();
  }, []);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(e.type === 'dragenter' || e.type === 'dragover');
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile?.name.endsWith('.csv')) {
      setFile(droppedFile);
      setUseGenerated(false);
      setError('');
    } else {
      setError('Please upload a CSV file');
    }
  }, []);

  const handleFileSelect = (e) => {
    const selected = e.target.files[0];
    if (selected?.name.endsWith('.csv')) {
      setFile(selected);
      setUseGenerated(false);
      setError('');
    } else {
      setError('Please upload a CSV file');
    }
  };

  const addApprover = () => setApprovers([...approvers, '']);
  const removeApprover = (index) => {
    if (approvers.length > 1) setApprovers(approvers.filter((_, i) => i !== index));
  };
  const updateApprover = (index, value) => {
    const normalized = value.trim().toLowerCase();
    if (normalized && approvers.some((a, i) => i !== index && a.trim().toLowerCase() === normalized)) {
      setError(`Reviewer "${value.trim()}" is already added`);
      return;
    }
    setError('');
    const updated = [...approvers];
    updated[index] = value;
    setApprovers(updated);
  };

  const handleTestCasesGenerated = (cases) => {
    // Preserve all columns from the AI response (don't strip to fixed headers)
    setTestCases(prev => [...prev, ...cases]);
    setUseGenerated(true);
    setFile(null);
  };

  // BRD file validation helper
  const isValidBrdFile = (f) => {
    const ext = f.name.toLowerCase().slice(f.name.lastIndexOf('.'));
    return VALID_BRD_EXTENSIONS.includes(ext);
  };

  const handleBrdFileSelect = (selectedFile) => {
    setBrdError('');
    if (!selectedFile) return;
    if (!isValidBrdFile(selectedFile)) {
      setBrdError('Please upload a PDF or Word (.docx) file');
      setBrdFile(null);
      return;
    }
    if (selectedFile.size > MAX_BRD_SIZE) {
      setBrdError('File exceeds the maximum allowed size of 20 MB');
      setBrdFile(null);
      return;
    }
    setBrdFile(selectedFile);
  };

  const handleBrdDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    const active = e.type === 'dragenter' || e.type === 'dragover';
    brdDragActiveRef.current = active;
    setBrdDragActive(active);
  }, []);

  const handleBrdDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    brdDragActiveRef.current = false;
    setBrdDragActive(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) handleBrdFileSelect(droppedFile);
  }, []);

  const handleBrdInputChange = (e) => {
    const selected = e.target.files[0];
    if (selected) handleBrdFileSelect(selected);
  };

  const handleBrdGenerate = async () => {
    if (!brdFile) return;
    setBrdGenerating(true);
    setBrdError('');
    setBrdElapsed(0);
    brdTimerRef.current = setInterval(() => setBrdElapsed(s => s + 1), 1000);
    const abortController = new AbortController();
    brdAbortRef.current = abortController;

    try {
      const base64String = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const arrayBuffer = reader.result;
          const bytes = new Uint8Array(arrayBuffer);
          let binary = '';
          for (let i = 0; i < bytes.length; i++) {
            binary += String.fromCharCode(bytes[i]);
          }
          resolve(btoa(binary));
        };
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsArrayBuffer(brdFile);
      });

      // Check if aborted during file read
      if (abortController.signal.aborted) throw new Error('Generation cancelled');

      const result = await generateTestCases(null, null, base64String, brdFile.name);
      const cases = result.testCases || [];
      if (cases.length === 0) {
        setBrdError('No test cases were generated from the document');
      } else {
        handleTestCasesGenerated(cases);
        setSelectedTile(null);
        setBrdFile(null);
      }
    } catch (err) {
      if (err.name === 'AbortError' || abortController.signal.aborted) {
        setBrdError('Generation cancelled');
      } else {
        setBrdError(err.message || 'Failed to generate test cases');
      }
    } finally {
      clearInterval(brdTimerRef.current);
      brdAbortRef.current = null;
      setBrdGenerating(false);
    }
  };

  const handleBrdCancel = () => {
    if (brdAbortRef.current) brdAbortRef.current.abort();
  };

  // Cleanup BRD timer on unmount
  useEffect(() => {
    return () => {
      clearInterval(brdTimerRef.current);
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const hasFile = file && !useGenerated;
    const hasTestCases = useGenerated && testCases.length > 0;

    if (!hasFile && !hasTestCases) {
      setError('Please upload a CSV file or generate test cases');
      return;
    }

    setShowFileNameDialog(true);
  };

  const doUpload = async (uploadFileObj) => {
    const validApprovers = approvers.filter(a => a.trim());
    if (validApprovers.length === 0) { setError('Add at least one approver'); setShowFileNameDialog(false); return; }
    setUploading(true);
    setError('');
    try {
      const result = await uploadFile(uploadFileObj, validApprovers, userStory || '');
      navigate(`/review/${result.fileId}`);
    } catch (err) {
      setError(`Upload failed: ${err.message}`);
    } finally {
      setUploading(false);
    }
  };

  const handleSubmitConfirm = async () => {
    const hasTestCases = useGenerated && testCases.length > 0;
    if (hasTestCases) {
      const dynamicHeaders = testCases.length > 0
        ? [...new Set(testCases.flatMap(tc => Object.keys(tc)))]
        : [];
      const csvString = Papa.unparse(testCases, { columns: dynamicHeaders });
      const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      const alias = currentUserAlias || 'unknown';
      const defaultName = `testcases-${alias}-${ts}.csv`;
      let finalName = customFileName.trim() || defaultName;
      if (!finalName.toLowerCase().endsWith('.csv')) finalName += '.csv';
      const uploadFileObj = new File([csvString], finalName, { type: 'text/csv' });
      setShowFileNameDialog(false);
      await doUpload(uploadFileObj);
    } else {
      setShowFileNameDialog(false);
      await doUpload(file);
    }
  };

  return (
    <div className="upload-container">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <h2 style={{ margin: 0 }}>New Review Request</h2>
        <div style={{ position: 'relative' }}>
          <button
            type="button"
            className="btn btn-success"
            onClick={() => setShowReviewerPanel(p => !p)}
            style={{ fontSize: '0.85rem' }}
          >
            👥 Required Reviewer ({approvers.filter(a => a.trim()).length})
          </button>
          {showReviewerPanel && (
            <div style={{
              position: 'absolute', top: '110%', right: 0, width: 320, background: 'var(--card-bg, #fff)',
              border: '1px solid var(--border, #e0e0e0)', borderRadius: 'var(--radius, 8px)',
              boxShadow: '0 8px 24px rgba(0,0,0,0.15)', padding: '16px', zIndex: 100
            }}>
              <div style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: 10 }}>Required Reviewers</div>
              {approvers.map((approver, index) => (
                <div key={index} style={{ display: 'flex', gap: 6, marginBottom: 6, alignItems: 'center' }}>
                  <input
                    type="text"
                    placeholder="Approver alias (e.g., username)"
                    value={approver}
                    onChange={(e) => updateApprover(index, e.target.value)}
                    className="input"
                    aria-label={`Approver ${index + 1}`}
                    style={{ flex: 1, fontSize: '0.83rem' }}
                  />
                  {approvers.length > 1 && (
                    <button type="button" onClick={() => removeApprover(index)} className="btn btn-danger btn-sm" style={{ padding: '4px 8px' }}>✕</button>
                  )}
                </div>
              ))}
              <button type="button" onClick={addApprover} className="btn btn-secondary btn-sm" style={{ marginTop: 4, fontSize: '0.8rem' }}>
                + Add Required Reviewer
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Tile Selector — choose generation method */}
      <div className="tile-selector" style={{ marginBottom: 20 }}>
        <p style={{ margin: '0 0 12px', fontSize: '0.9rem', color: 'var(--text)' }}>
          🤖 Use Amazon Bedrock AI to generate test cases
        </p>
        <div className="tile-selector-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {/* User Story Tile */}
          <div
            className={`tile-card ${selectedTile === 'user-story' ? 'tile-card-selected' : ''}`}
            onClick={() => {
              setSelectedTile('user-story');
              setGenerateDialogOpen(true);
            }}
            role="button"
            tabIndex={0}
            aria-label="Generate from User Story"
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedTile('user-story'); setGenerateDialogOpen(true); } }}
          >
            <span style={{ fontSize: '1.5rem', marginBottom: 4 }}>📝</span>
            <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>User Story</span>
            <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>Paste a user story or BRD text</span>
          </div>

          {/* BRD Upload Tile */}
          <div
            className={`tile-card ${selectedTile === 'brd-upload' ? 'tile-card-selected' : ''}`}
            onClick={() => setSelectedTile('brd-upload')}
            role="button"
            tabIndex={0}
            aria-label="Upload BRD Document"
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedTile('brd-upload'); } }}
          >
            <span style={{ fontSize: '1.5rem', marginBottom: 4 }}>📄</span>
            <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>BRD Upload</span>
            <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>Upload a PDF or Word document</span>
          </div>
        </div>

        {/* BRD Upload Area — shown when BRD tile is selected */}
        {selectedTile === 'brd-upload' && (
          <div className="brd-upload-area" style={{ marginTop: 14 }}>
            {brdGenerating ? (
              /* Progress indicator during generation */
              <div className="brd-progress" style={{ textAlign: 'center', padding: '24px 16px' }}>
                <div style={{ fontSize: '1.2rem', marginBottom: 8 }}>⏳</div>
                <p style={{ margin: '0 0 4px', fontSize: '0.9rem', fontWeight: 500 }}>
                  Parsing document and generating test cases...
                </p>
                <p style={{ margin: '0 0 12px', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                  Elapsed: {brdElapsed}s
                </p>
                <button
                  type="button"
                  className="btn btn-danger btn-sm"
                  onClick={handleBrdCancel}
                >
                  ✕ Cancel
                </button>
              </div>
            ) : (
              <>
                {/* Drag-and-drop file area */}
                <div
                  className={`brd-drop-zone ${brdDragActive ? 'brd-drop-zone-active' : ''} ${brdFile ? 'brd-drop-zone-has-file' : ''}`}
                  onDragEnter={handleBrdDrag}
                  onDragLeave={handleBrdDrag}
                  onDragOver={handleBrdDrag}
                  onDrop={handleBrdDrop}
                  onClick={() => document.getElementById('brd-file-input').click()}
                  role="button"
                  tabIndex={0}
                  aria-label="Upload BRD document"
                  style={{
                    border: '2px dashed var(--border)', borderRadius: 'var(--radius)',
                    padding: '28px 16px', textAlign: 'center', cursor: 'pointer',
                    background: brdDragActive ? 'var(--primary-light)' : brdFile ? '#edf7f0' : 'var(--surface)',
                    borderColor: brdDragActive ? 'var(--primary)' : brdFile ? 'var(--success)' : 'var(--border)',
                    transition: 'all 0.2s'
                  }}
                >
                  <input
                    id="brd-file-input"
                    type="file"
                    accept=".pdf,.docx"
                    onChange={handleBrdInputChange}
                    style={{ display: 'none' }}
                  />
                  {brdFile ? (
                    <div className="brd-file-info">
                      <span style={{ fontSize: '1.3rem' }}>📄</span>
                      <span className="brd-file-name" style={{ fontWeight: 500 }}>{brdFile.name}</span>
                      <span className="brd-file-size" style={{ fontSize: '0.83rem', color: 'var(--text-secondary)' }}>
                        ({(brdFile.size / 1024).toFixed(1)} KB)
                      </span>
                    </div>
                  ) : (
                    <div>
                      <span style={{ fontSize: '1.5rem', display: 'block', marginBottom: 6 }}>⬆️</span>
                      <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                        Drag & drop a PDF or Word (.docx) file here, or click to browse
                      </p>
                    </div>
                  )}
                </div>

                {/* BRD error message */}
                {brdError && (
                  <div className="brd-error" role="alert" style={{
                    background: '#f8d7da', color: '#721c24', padding: '8px 12px',
                    borderRadius: 'var(--radius)', marginTop: 8, fontSize: '0.85rem'
                  }}>
                    {brdError}
                  </div>
                )}

                {/* Generate button — shown when valid file selected */}
                {brdFile && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleBrdGenerate}
                    style={{ marginTop: 12, width: '100%' }}
                  >
                    🚀 Generate Test Cases
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Test Cases Table */}
      {testCases.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <h3 style={{ margin: 0, fontSize: '1rem' }}>Generated Test Cases</h3>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-secondary btn-sm" onClick={() => setGenerateDialogOpen(true)}>
                🤖 Generate More
              </button>
              <button className="btn btn-danger btn-sm" onClick={() => { setTestCases([]); setUseGenerated(false); }}>
                Clear All
              </button>
            </div>
          </div>
          <TestCaseTable testCases={testCases} onUpdate={setTestCases} />
          {/* Refine with AI button — opens side panel */}
          <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
            <button
              className="btn btn-primary"
              onClick={() => setRefinePanelOpen(true)}
              style={{ fontSize: '0.85rem' }}
            >
              ✨ Refine with AI
            </button>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Only show file upload if no generated test cases, or allow switching */}
        {!useGenerated || testCases.length === 0 ? (
          <>
            <div style={{ textAlign: 'center', margin: '12px 0', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
              — or upload a CSV file directly —
            </div>
            <div
              className={`drop-zone ${dragActive ? 'active' : ''} ${file ? 'has-file' : ''}`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={() => document.getElementById('file-input').click()}
              role="button"
              tabIndex={0}
              aria-label="Upload CSV file"
            >
              <input
                id="file-input"
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
              {file ? (
                <div className="file-selected">
                  <span className="file-icon">📄</span>
                  <span>{file.name} ({(file.size / 1024).toFixed(1)} KB)</span>
                </div>
              ) : (
                <div className="drop-prompt">
                  <span className="upload-icon">⬆️</span>
                  <p>Drag & drop a CSV file here, or click to browse</p>
                </div>
              )}
            </div>
          </>
        ) : (
          <div style={{
            background: 'var(--surface-warm)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius)', padding: 12, marginBottom: 16, fontSize: '0.85rem',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between'
          }}>
            <span>📄 Using {testCases.length} generated test case{testCases.length !== 1 ? 's' : ''} as CSV</span>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setUseGenerated(false); setTestCases([]); }}>
              Switch to File Upload
            </button>
          </div>
        )}

        {error && <div className="error-message" role="alert">{error}</div>}

        <button type="submit" className="btn btn-primary" disabled={uploading}>
          {uploading ? 'Uploading...' : 'Submit for Review'}
        </button>
      </form>

      <GenerateTestCasesDialog
        isOpen={generateDialogOpen}
        onClose={() => setGenerateDialogOpen(false)}
        onTestCasesGenerated={handleTestCasesGenerated}
        onUserStoryCapture={setUserStory}
      />

      {/* Refine with AI side panel */}
      <RefineChatPanel
        isOpen={refinePanelOpen}
        onClose={() => setRefinePanelOpen(false)}
        testCases={testCases}
        onUpdate={setTestCases}
        userStory={userStory}
        onUserStoryChange={setUserStory}
      />

      {/* Submit Confirmation Dialog */}
      {showFileNameDialog && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={() => setShowFileNameDialog(false)}>
          <div style={{ background: 'var(--card-bg, #fff)', borderRadius: 'var(--radius, 8px)', padding: '24px 28px', width: 440, maxWidth: '90vw', boxShadow: '0 8px 30px rgba(0,0,0,0.2)' }}
            onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 16px', fontSize: '1.05rem' }}>📋 Confirm Submission</h3>

            {/* File name — only for generated test cases */}
            {useGenerated && testCases.length > 0 && (
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: '0.83rem', fontWeight: 600, marginBottom: 6, color: 'var(--text-secondary)' }}>File Name (optional)</div>
                <input
                  type="text"
                  className="input"
                  value={customFileName}
                  onChange={e => setCustomFileName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleSubmitConfirm(); }}
                  placeholder={`testcases-${currentUserAlias || 'alias'}-2025-01-01T00-00-00.csv`}
                  style={{ width: '100%', fontSize: '0.85rem', marginBottom: 4 }}
                  aria-label="Custom file name"
                  autoFocus
                />
                <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                  Leave blank for auto-generated name. ".csv" appended if omitted.
                </span>
              </div>
            )}

            {/* Reviewers summary */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: '0.83rem', fontWeight: 600, marginBottom: 6, color: 'var(--text-secondary)' }}>Required Reviewers</div>
              {approvers.filter(a => a.trim()).length === 0 ? (
                <div style={{ fontSize: '0.83rem', color: 'var(--danger, #c0392b)', padding: '8px 10px', background: '#fde8e8', borderRadius: 6 }}>
                  ⚠ 0 reviewers added — add at least one reviewer before submitting.
                </div>
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {approvers.filter(a => a.trim()).map((a, i) => (
                    <span key={i} style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      background: 'var(--primary-light, #e8f0fe)', color: 'var(--text-primary, #333)',
                      padding: '4px 10px', borderRadius: 12, fontSize: '0.82rem'
                    }}>
                      👤 {a.trim()}
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 18 }}>
              <button type="button" className="btn btn-secondary" onClick={() => setShowFileNameDialog(false)}>Cancel</button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSubmitConfirm}
                disabled={uploading || approvers.filter(a => a.trim()).length === 0}
              >
                {uploading ? 'Submitting...' : 'Submit for Review'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default FileUpload;
